import i18n from "../../i18n";
import React, { useMemo } from "react";
import {
  Stack,
  SimpleGrid,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Switch,
  Text,
  Box,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import _ from "lodash";
import RemoteSelect2 from "../remoteSelect2";
import {
  isFileServiceFieldType,
  isMultiFileServiceFieldType,
  getServiceFieldNumberPrecision,
  isRefServiceFieldType,
  getServiceRefConfig,
} from "../../data/orderServiceFieldTypes";
import { DATA_ORDER_STATUS_OPTIONS } from "../../data/options";
import ServiceTypeFieldFileInput from "./ServiceTypeFieldFileInput";

const parseDateValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const AddressFieldsInput = ({ value = {}, onChange, label, disabled }) => {
  const address = value && typeof value === "object" ? value : {};
  const update = (key, next) => onChange?.({ ...address, [key]: next });

  return (
    <Stack spacing="xs">
      <Text size="sm" weight={500}>
        {label}
      </Text>
      <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
        <TextInput
          label="Line 1"
          value={address.line1 ?? ""}
          disabled={disabled}
          onChange={(e) => update("line1", e.target.value)}
        />
        <TextInput
          label="Line 2"
          value={address.line2 ?? ""}
          disabled={disabled}
          onChange={(e) => update("line2", e.target.value)}
        />
        <TextInput
          label="City"
          value={address.city ?? ""}
          disabled={disabled}
          onChange={(e) => update("city", e.target.value)}
        />
        <TextInput
          label="State"
          value={address.state ?? ""}
          disabled={disabled}
          onChange={(e) => update("state", e.target.value)}
        />
        <TextInput
          label="Zip"
          value={address.zip ?? ""}
          disabled={disabled}
          onChange={(e) => update("zip", e.target.value)}
        />
        <TextInput
          label="Country"
          value={address.country ?? ""}
          disabled={disabled}
          onChange={(e) => update("country", e.target.value)}
        />
      </SimpleGrid>
    </Stack>
  );
};

const ServiceTypeFieldsInput = ({
  form,
  name = "fieldValues",
  fieldsName = "serviceTypeSnapShot.fields",
  readOnly = false,
  disabled = false,
}) => {
  const definitions = useMemo(() => {
    const fields = _.get(form?.values, fieldsName) || [];
    return [...fields].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    );
  }, [form?.values, fieldsName]);

  const values = _.get(form?.values, name) || {};

  if (!definitions.length) return null;

  const updateValue = (key, nextValue) => {
    form.setFieldValue(name, {
      ...values,
      [key]: nextValue,
    });
  };

  const renderField = (def) => {
    const label = def.cname ? `${def.label} / ${def.cname}` : def.label;
    const value = values?.[def.key];
    const isRequired = !!def.required;

    if (def.orderFieldPath === "address") {
      return (
        <AddressFieldsInput
          key={def.key}
          label={`${label}${isRequired ? " *" : ""}`}
          value={value || {}}
          disabled={disabled || readOnly}
          onChange={(next) => updateValue(def.key, next)}
        />
      );
    }

    if (def.fieldType === "TEXTAREA") {
      return (
        <Textarea
          key={def.key}
          label={label}
          required={isRequired}
          value={value ?? ""}
          disabled={disabled || readOnly}
          minRows={2}
          onChange={(e) => updateValue(def.key, e.target.value)}
        />
      );
    }

    if (
      def.fieldType === "INTEGER" ||
      def.fieldType === "DECIMAL_2" ||
      def.fieldType === "DECIMAL_4"
    ) {
      return (
        <NumberInput
          key={def.key}
          label={label}
          required={isRequired}
          value={value ?? ""}
          precision={getServiceFieldNumberPrecision(def.fieldType)}
          disabled={disabled || readOnly}
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (def.fieldType === "DATE") {
      return (
        <DatePicker
          key={def.key}
          label={label}
          required={isRequired}
          value={parseDateValue(value)}
          disabled={disabled || readOnly}
          allowFreeInput
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (def.fieldType === "SELECT") {
      return (
        <Select
          key={def.key}
          label={label}
          required={isRequired}
          value={value ?? null}
          disabled={disabled || readOnly}
          data={(def.selectOptions || []).map((o) => {
            const stored = o.value ?? o.label;
            const display = o.cname ? `${o.label} / ${o.cname}` : o.label;
            return { value: stored, label: display };
          })}
          searchable
          clearable
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (def.fieldType === "SELECT_ORDER_STATUS") {
      return (
        <Select
          key={def.key}
          label={label}
          required={isRequired}
          value={value ?? null}
          disabled={disabled || readOnly}
          data={DATA_ORDER_STATUS_OPTIONS}
          searchable
          clearable
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (isRefServiceFieldType(def.fieldType)) {
      const config = getServiceRefConfig(def.fieldType);
      if (!config) return null;
      return (
        <RemoteSelect2
          key={def.key}
          label={label}
          apiEntity={config.apiEntity}
          searchFields={config.searchFields}
          labelRender={config.labelRender}
          pageSize={config.pageSize}
          valueField="_id"
          clearable
          disabled={disabled || readOnly}
          value={value}
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (def.fieldType === "BOOLEAN") {
      return (
        <Switch
          key={def.key}
          label={label}
          checked={!!value}
          disabled={disabled || readOnly}
          onChange={(e) => updateValue(def.key, e.currentTarget.checked)}
        />
      );
    }

    if (isFileServiceFieldType(def.fieldType)) {
      return (
        <ServiceTypeFieldFileInput
          key={def.key}
          form={form}
          fieldKey={def.key}
          label={`${label}${isRequired ? " *" : ""}`}
          multiple={isMultiFileServiceFieldType(def.fieldType)}
          readOnly={readOnly}
          disabled={disabled}
        />
      );
    }

    return (
      <TextInput
        key={def.key}
        label={label}
        required={isRequired}
        value={value ?? ""}
        disabled={disabled || readOnly}
        onChange={(e) => updateValue(def.key, e.target.value)}
      />
    );
  };

  return (
    <Stack spacing="md">
      <Text size="sm" weight={600}>{i18n.t("Service Fields")}</Text>
      {definitions.map((def) => (
        <Box key={def.key}>{renderField(def)}</Box>
      ))}
    </Stack>
  );
};

export default ServiceTypeFieldsInput;
